import { MessageUiComponentResultOutcome } from "@redotech/crdb-enums/shopper-ai/message-ui-component-result-outcome";
import { MessageUiComponentStatus } from "@redotech/crdb-enums/shopper-ai/message-ui-component-status";
import { zodToEffectSchema } from "@redotech/effect-api-shared-common/zod";
import { ShippingAddressToUpdateSchema } from "@redotech/redo-agents-cards/support-agents/confirm-update-order-shipping-address";
import { Schema } from "effect";
import {
  MessageId,
  MessageUiComponentId,
  MessageUiComponentResultId,
} from "../../../../ids/definitions";
import { DateFromDb } from "../../../../schema/helpers";

/**
 * An `action` card runs its action once the customer approves it. A `display`
 * card only shows something, and collects a result per button press.
 */
export const UiComponentKind = Schema.Literal("action", "display");
export type UiComponentKind = typeof UiComponentKind.Type;

export const UiComponentType = Schema.Literal(
  "cancelOrder",
  "updateShippingAddress",
  "updateOrder",
  "return",
  "signIn",
  "products",
  "orderTracking",
  "discountCode",
  "productComparison",
  "wishlist",
  "backInStock",
  "resendReturnLabel",
);
export type UiComponentType = typeof UiComponentType.Type;

export const UI_COMPONENT_KINDS = {
  cancelOrder: "action",
  updateShippingAddress: "action",
  updateOrder: "action",
  return: "action",
  signIn: "action",
  products: "display",
  orderTracking: "display",
  discountCode: "display",
  productComparison: "display",
  wishlist: "display",
  backInStock: "action",
  resendReturnLabel: "action",
} as const satisfies Record<UiComponentType, UiComponentKind>;

/** The card types that only show something and take no decision. */
export type DisplayUiComponentType = {
  [Type in UiComponentType]: (typeof UI_COMPONENT_KINDS)[Type] extends "display"
    ? Type
    : never;
}[UiComponentType];

export const UiComponentStatus = Schema.Enums(MessageUiComponentStatus);
export type UiComponentStatus = typeof UiComponentStatus.Type;

export const UiComponentResultOutcome = Schema.Enums(
  MessageUiComponentResultOutcome,
);
export type UiComponentResultOutcome = typeof UiComponentResultOutcome.Type;

/** Whether the customer approved an action card. Expiry is not a decision. */
export const UiComponentDecision = Schema.Literal(
  MessageUiComponentStatus.Approved,
  MessageUiComponentStatus.Denied,
);
export type UiComponentDecision = typeof UiComponentDecision.Type;

/** The `expired_reason` of a pending card read after its `expires_at`. */
export const UI_COMPONENT_TIMED_OUT_REASON = "timedOut";

/** The `expired_reason` of a card pressed after a person took over its ticket, when the agent's offers lapse. */
export const UI_COMPONENT_HANDED_OFF_REASON = "handedOff";

/** The `expired_reason` of a pending card a newer card in its conversation replaced. */
export const UI_COMPONENT_REPLACED_REASON = "replaced";

/** The `expired_reason` of a pending card whose order was cancelled. */
export const UI_COMPONENT_ORDER_CANCELLED_REASON = "orderCancelled";

const actionFields = { status: UiComponentStatus, expiresAt: DateFromDb };

const PositiveInt = Schema.Number.pipe(Schema.int(), Schema.positive());

const displayFields = {
  customerInput: Schema.Null,
  status: Schema.Null,
  expiresAt: Schema.Null,
};

/** `customerInput` is a partial of the fields the customer may edit; these cards have none. */
const NoEditableFields = Schema.Struct({});

/*
 * Inputs and results are read back to the agent as tool calls, so they keep to
 * JSON: mutable arrays, and null rather than an absent field.
 */

/** A major-unit amount, such as `"25.00"`, and its ISO 4217 currency code. Never pre-formatted. */
export const UiComponentMoney = Schema.Struct({
  amount: Schema.String.pipe(Schema.pattern(/^\d+(\.\d+)?$/u)),
  currencyCode: Schema.String.pipe(Schema.pattern(/^[A-Z]{3}$/u)),
});
export type UiComponentMoney = typeof UiComponentMoney.Type;

/*
 * `display` is a snapshot of what the card shows, taken when it is posted. It
 * holds nothing `input` or `customerInput` already carries.
 */

export const UiComponentLineItem = Schema.Struct({
  title: Schema.String,
  variantTitle: Schema.NullOr(Schema.String),
  quantity: Schema.Number,
  unitPrice: Schema.NullOr(UiComponentMoney),
  imageUrl: Schema.NullOr(Schema.String),
});
export type UiComponentLineItem = typeof UiComponentLineItem.Type;

/** An amount taken off a total, such as a discount, so it is always negative. */
const UiComponentDeduction = UiComponentMoney.pipe(
  Schema.filter((money) => Number(money.amount) < 0, {
    message: () => "A deduction is a negative amount",
  }),
);

/** Optional rows are omitted on cards posted before the cancel-order design. */
const CancelOrderDisplay = Schema.Struct({
  orderName: Schema.String,
  items: Schema.Array(UiComponentLineItem),
  /** What the customer is refunded, shown as "Refund owed". */
  total: UiComponentMoney,
  /** Item prices before tax. The card sums line prices when this is absent. */
  subtotal: Schema.optional(Schema.NullOr(UiComponentMoney)),
  /** What discounts took off the subtotal. */
  discount: Schema.optional(Schema.NullOr(UiComponentDeduction)),
  /** Shipping after any shipping discount. */
  shipping: Schema.optional(Schema.NullOr(UiComponentMoney)),
  tax: Schema.optional(Schema.NullOr(UiComponentMoney)),
  /** Whether prices already include `tax`, so it isn't added to the total. */
  taxIncluded: Schema.optional(Schema.Boolean),
  /** Where the confirmation is emailed after the order is cancelled. */
  email: Schema.optional(Schema.NullOr(Schema.String)),
});

const CancelOrderInput = Schema.Struct({
  orderId: Schema.String,
  cancellationReason: Schema.String,
});

const CancelOrderComponent = Schema.Struct({
  componentType: Schema.Literal("cancelOrder"),
  input: CancelOrderInput,
  display: CancelOrderDisplay,
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

export const UiComponentShippingAddress = zodToEffectSchema(
  ShippingAddressToUpdateSchema,
);
export type UiComponentShippingAddress = typeof UiComponentShippingAddress.Type;

const UpdateShippingAddressEditableFields = Schema.Struct({
  updatedShippingAddress: UiComponentShippingAddress,
});

const UpdateShippingAddressCustomerInput = Schema.partialWith(
  UpdateShippingAddressEditableFields,
  { exact: true },
);

const UpdateShippingAddressInput = Schema.Struct({
  orderId: Schema.String,
  ...UpdateShippingAddressEditableFields.fields,
});

/** The address the order ships to today; the new one is the card's input. */
const UpdateShippingAddressDisplay = Schema.Struct({
  orderName: Schema.String,
  currentAddress: Schema.NullOr(UiComponentShippingAddress),
});

const UpdateShippingAddressComponent = Schema.Struct({
  componentType: Schema.Literal("updateShippingAddress"),
  input: UpdateShippingAddressInput,
  display: UpdateShippingAddressDisplay,
  customerInput: Schema.NullOr(UpdateShippingAddressCustomerInput),
  ...actionFields,
});

const UpdateOrderEditableFields = Schema.Struct({
  items: Schema.mutable(
    Schema.Array(
      Schema.Struct({
        variantId: Schema.String,
        quantity: PositiveInt,
        action: Schema.Literal("add", "remove"),
      }),
    ),
  ),
});

const UpdateOrderCustomerInput = Schema.partialWith(UpdateOrderEditableFields, {
  exact: true,
});

const UpdateOrderInput = Schema.Struct({
  orderId: Schema.String,
  ...UpdateOrderEditableFields.fields,
});

export const UiComponentOrderProductVariant = Schema.Struct({
  variantId: Schema.String,
  title: Schema.String,
  unitPrice: Schema.NullOr(UiComponentMoney),
  /** How many of this variant the order holds today. */
  currentQuantity: Schema.Number,
});
export type UiComponentOrderProductVariant =
  typeof UiComponentOrderProductVariant.Type;

export const UiComponentOrderProduct = Schema.Struct({
  title: Schema.String,
  imageUrl: Schema.NullOr(Schema.String),
  variants: Schema.Array(UiComponentOrderProductVariant),
});
export type UiComponentOrderProduct = typeof UiComponentOrderProduct.Type;

/**
 * The product behind each variant in `input.items`. A product being added lists
 * every variant the customer may switch to; one being removed lists its own.
 */
const UpdateOrderDisplay = Schema.Struct({
  orderName: Schema.String,
  products: Schema.Array(UiComponentOrderProduct),
});

const UpdateOrderComponent = Schema.Struct({
  componentType: Schema.Literal("updateOrder"),
  input: UpdateOrderInput,
  display: UpdateOrderDisplay,
  customerInput: Schema.NullOr(UpdateOrderCustomerInput),
  ...actionFields,
});

const ReturnInput = Schema.Struct({
  orderId: Schema.String,
  quoteId: Schema.NullOr(Schema.String),
});

/** The quoted return: what goes back and what the customer gets or owes. */
const ReturnDisplay = Schema.Struct({
  orderName: Schema.String,
  items: Schema.Array(UiComponentLineItem),
  refundAmount: Schema.NullOr(UiComponentMoney),
  storeCreditAmount: Schema.NullOr(UiComponentMoney),
  amountOwed: Schema.NullOr(UiComponentMoney),
  returnShippingFee: Schema.NullOr(UiComponentMoney),
});

const ReturnComponent = Schema.Struct({
  componentType: Schema.Literal("return"),
  input: ReturnInput,
  display: ReturnDisplay,
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

/** `reason` finishes the sentence "Sign in ...", such as "to cancel your order". */
const SignInInput = Schema.Struct({ reason: Schema.String });

/** `email` prefills the form when the customer already gave one. */
const SignInDisplay = Schema.Struct({ email: Schema.NullOr(Schema.String) });

const SignInComponent = Schema.Struct({
  componentType: Schema.Literal("signIn"),
  input: SignInInput,
  display: SignInDisplay,
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

export const UiComponentProductVariant = Schema.Struct({
  variantId: Schema.String,
  title: Schema.String,
  /**
   * Its value for each of the product's options, such as Size: M, so a variant
   * can be picked one option at a time. Cards saved before this field have only `title`.
   */
  selectedOptions: Schema.optional(
    Schema.Array(Schema.Struct({ name: Schema.String, value: Schema.String })),
  ),
  price: UiComponentMoney,
  available: Schema.Boolean,
  /** Images of this variant, shown first when it is selected. */
  images: Schema.optional(Schema.Array(Schema.String)),
});
export type UiComponentProductVariant = typeof UiComponentProductVariant.Type;

export const UiComponentProduct = Schema.Struct({
  productId: Schema.String,
  title: Schema.String,
  imageUrl: Schema.NullOr(Schema.String),
  /** Every product image, main image first. Cards saved before this field have only `imageUrl`. */
  images: Schema.optional(Schema.Array(Schema.String)),
  productUrl: Schema.NullOr(Schema.String),
  variants: Schema.Array(UiComponentProductVariant),
});
export type UiComponentProduct = typeof UiComponentProduct.Type;

const ProductsDisplay = Schema.Struct({
  products: Schema.Array(UiComponentProduct),
});

const ProductsComponent = Schema.Struct({
  componentType: Schema.Literal("products"),
  input: Schema.Struct({
    productIds: Schema.mutable(Schema.Array(Schema.String)),
  }),
  display: ProductsDisplay,
  ...displayFields,
});

/** One package of an order. Dates are ISO strings; `status` is the tracker's shipment status. */
export const UiComponentOrderShipment = Schema.Struct({
  status: Schema.String,
  carrier: Schema.NullOr(Schema.String),
  trackingNumber: Schema.NullOr(Schema.String),
  trackingUrl: Schema.NullOr(Schema.String),
  estimatedDelivery: Schema.NullOr(Schema.String),
  deliveredAt: Schema.NullOr(Schema.String),
  lastEvent: Schema.NullOr(
    Schema.Struct({ message: Schema.String, at: Schema.String }),
  ),
  items: Schema.Array(
    Schema.Struct({
      title: Schema.String,
      variantTitle: Schema.NullOr(Schema.String),
      quantity: Schema.Number,
    }),
  ),
});
export type UiComponentOrderShipment = typeof UiComponentOrderShipment.Type;

const OrderTrackingComponent = Schema.Struct({
  componentType: Schema.Literal("orderTracking"),
  input: Schema.Struct({ orderId: Schema.String }),
  display: Schema.Struct({
    orderName: Schema.String,
    shipments: Schema.Array(UiComponentOrderShipment),
  }),
  ...displayFields,
});

const DiscountCodeComponent = Schema.Struct({
  componentType: Schema.Literal("discountCode"),
  input: Schema.Struct({ discountCode: Schema.String }),
  /** The discount's name and what it gets the customer, as the generate tool returned them. */
  display: Schema.Struct({
    title: Schema.String,
    description: Schema.NullOr(Schema.String),
    /** The code's terms; absent on cards posted before the card showed them. */
    terms: Schema.optional(
      Schema.Struct({
        /** ISO date and time the code stops working; null when it doesn't. */
        expiresAt: Schema.NullOr(Schema.String),
        totalUsageLimit: Schema.NullOr(PositiveInt),
        oncePerCustomer: Schema.Boolean,
        combinesWithOtherDiscounts: Schema.NullOr(Schema.Boolean),
      }),
    ),
  }),
  ...displayFields,
});

const UiComponentComparedProduct = Schema.Struct({
  productId: Schema.String,
  title: Schema.String,
  imageUrl: Schema.NullOr(Schema.String),
  productUrl: Schema.NullOr(Schema.String),
  /** The lowest variant price, shown as "From". */
  price: Schema.NullOr(UiComponentMoney),
});

/** Two products side by side; each feature lists one value per product, in the products' order. */
const ProductComparisonComponent = Schema.Struct({
  componentType: Schema.Literal("productComparison"),
  /** The show tool's own parameters, with the product it compared against filled in. */
  input: Schema.Struct({
    productId: Schema.String,
    similarProductId: Schema.String,
  }),
  display: Schema.Struct({
    products: Schema.Array(UiComponentComparedProduct),
    features: Schema.Array(
      Schema.Struct({
        name: Schema.String,
        values: Schema.Array(Schema.String),
      }),
    ),
    recommendation: Schema.NullOr(Schema.String),
  }),
  ...displayFields,
});

export const UiComponentWishlistItem = Schema.Struct({
  productId: Schema.String,
  variantId: Schema.NullOr(Schema.String),
  title: Schema.String,
  variantTitle: Schema.NullOr(Schema.String),
  imageUrl: Schema.NullOr(Schema.String),
  price: Schema.NullOr(UiComponentMoney),
  productUrl: Schema.NullOr(Schema.String),
});
export type UiComponentWishlistItem = typeof UiComponentWishlistItem.Type;

/** The signed-in customer's saved items; `totalCount` counts any past the ones shown. */
const WishlistComponent = Schema.Struct({
  componentType: Schema.Literal("wishlist"),
  input: Schema.Struct({}),
  display: Schema.Struct({
    items: Schema.Array(UiComponentWishlistItem),
    totalCount: Schema.Number,
  }),
  ...displayFields,
});

/** `customerEmail` is the address the agent was asked to use; null means the one on file. */
const BackInStockInput = Schema.Struct({
  productId: Schema.String,
  variantIds: Schema.mutable(Schema.Array(Schema.String)),
  customerEmail: Schema.NullOr(Schema.String),
});

const BackInStockComponent = Schema.Struct({
  componentType: Schema.Literal("backInStock"),
  input: BackInStockInput,
  /** `email` is where the notification goes, so the customer sees it before agreeing. */
  display: Schema.Struct({
    productTitle: Schema.String,
    imageUrl: Schema.NullOr(Schema.String),
    variantTitles: Schema.Array(Schema.String),
    email: Schema.String,
  }),
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

const ResendReturnLabelInput = Schema.Struct({ orderId: Schema.String });

/** `email` is where the label goes, when the order has one. */
const ResendReturnLabelComponent = Schema.Struct({
  componentType: Schema.Literal("resendReturnLabel"),
  input: ResendReturnLabelInput,
  display: Schema.Struct({
    orderName: Schema.String,
    email: Schema.NullOr(Schema.String),
  }),
  customerInput: Schema.NullOr(NoEditableFields),
  ...actionFields,
});

/** A press on a product carousel adds one variant of one product to the cart. */
const ProductPressInput = Schema.Struct({
  productId: Schema.String,
  variantId: Schema.String,
  quantity: PositiveInt,
});

/** What a card shows and runs, keyed on `componentType`. */
export const UiComponentContent = Schema.Union(
  CancelOrderComponent,
  UpdateShippingAddressComponent,
  UpdateOrderComponent,
  ReturnComponent,
  SignInComponent,
  ProductsComponent,
  OrderTrackingComponent,
  DiscountCodeComponent,
  ProductComparisonComponent,
  WishlistComponent,
  BackInStockComponent,
  ResendReturnLabelComponent,
);
export type UiComponentContent = typeof UiComponentContent.Type;

export const NewUiComponent = Schema.extend(
  UiComponentContent,
  Schema.Struct({
    requestId: Schema.String,
    /** The show tool call that posted the card. */
    toolCallId: Schema.String,
  }),
);
export type NewUiComponent = typeof NewUiComponent.Type;

/** A stored card. A pending card read after `expiresAt` reads as expired. */
export const UiComponent = Schema.extend(
  NewUiComponent,
  Schema.Struct({
    id: MessageUiComponentId,
    messageId: MessageId,
    expiredReason: Schema.NullOr(Schema.String),
    decidedAt: Schema.NullOr(Schema.DateFromSelf),
    createdAt: Schema.DateFromSelf,
  }),
);
export type UiComponent = typeof UiComponent.Type;

/** The edits a customer made to a card before approving it, keyed on the card's `componentType`. */
export const UiComponentCustomerEdits = Schema.Union(
  Schema.Struct({
    componentType: Schema.Literal("updateShippingAddress"),
    customerInput: UpdateShippingAddressCustomerInput,
  }),
  Schema.Struct({
    componentType: Schema.Literal("updateOrder"),
    customerInput: UpdateOrderCustomerInput,
  }),
);
export type UiComponentCustomerEdits = typeof UiComponentCustomerEdits.Type;

/*
 * What each card's action returns when it succeeds. A first cut from the
 * action tools' outputs; each card's tool MR settles its own.
 */
const CancelOrderResult = Schema.Struct({
  refundAmount: Schema.NullOr(UiComponentMoney),
});

const UpdateShippingAddressResult = Schema.Struct({
  updatedAddress: UiComponentShippingAddress,
});

const UpdateOrderResult = Schema.Struct({
  amountToPay: Schema.NullOr(UiComponentMoney),
  amountToRefund: Schema.NullOr(UiComponentMoney),
  paymentUrl: Schema.NullOr(Schema.String),
});

const ReturnResult = Schema.Struct({
  /** Null when payment or a saved card is needed first, and `portalUrl` is where the customer finishes. */
  returnId: Schema.NullOr(Schema.String),
  portalUrl: Schema.NullOr(Schema.String),
  labelEmailedTo: Schema.NullOr(Schema.String),
  /** The return's status; one that needs review gets no label or compensation until approved. */
  status: Schema.optional(Schema.String),
  requiresReview: Schema.optional(Schema.Boolean),
  /** What the return tool tells the agent to say and not promise. */
  note: Schema.optional(Schema.String),
});

/** The agent treats `email` as a verified sign-in, so the server sets it from the token, never the client. */
const SignInResult = Schema.Struct({ email: Schema.String });

/** Adding to cart happens in the customer's browser and returns nothing. */
const EmptyResult = Schema.Struct({});

const resultContent = <
  const ComponentType extends UiComponentType,
  Input extends Schema.Schema.Any,
  Result extends Schema.Schema.Any,
>(
  componentType: ComponentType,
  effectiveInput: Input,
  result: Result,
) =>
  Schema.Struct({
    componentType: Schema.Literal(componentType),
    effectiveInput,
    /** Null unless the action succeeded. */
    result: Schema.NullOr(result),
  });

/**
 * What a button press ran and returned, keyed on the `componentType` of the
 * card it answers. An action card runs its input merged with the customer's edits.
 */
export const UiComponentResultContent = Schema.Union(
  resultContent("cancelOrder", CancelOrderInput, CancelOrderResult),
  resultContent(
    "updateShippingAddress",
    UpdateShippingAddressInput,
    UpdateShippingAddressResult,
  ),
  resultContent("updateOrder", UpdateOrderInput, UpdateOrderResult),
  resultContent("return", ReturnInput, ReturnResult),
  resultContent("signIn", SignInInput, SignInResult),
  resultContent("products", ProductPressInput, EmptyResult),
  resultContent("backInStock", BackInStockInput, EmptyResult),
  resultContent(
    "resendReturnLabel",
    ResendReturnLabelInput,
    Schema.Struct({
      labelUrls: Schema.mutable(Schema.Array(Schema.String)),
      /** Drop-off QR codes, which a carrier location scans instead of a printed label. */
      qrCodeUrls: Schema.mutable(Schema.Array(Schema.String)),
      /** Why some of the order's returns got no label yet, or null when every one did. */
      notResent: Schema.NullOr(Schema.String),
    }),
  ),
);
export type UiComponentResultContent = typeof UiComponentResultContent.Type;

export const NewUiComponentResult = Schema.extend(
  UiComponentResultContent,
  Schema.Struct({
    requestId: Schema.String,
    /** The button pressed: `approve`, `deny`, `addToCart`, and so on. */
    action: Schema.String,
    outcome: UiComponentResultOutcome,
    error: Schema.NullOr(Schema.String),
  }),
);
export type NewUiComponentResult = typeof NewUiComponentResult.Type;

export const UiComponentResult = Schema.extend(
  NewUiComponentResult,
  Schema.Struct({
    id: MessageUiComponentResultId,
    messageId: MessageId,
    createdAt: Schema.DateFromSelf,
  }),
);
export type UiComponentResult = typeof UiComponentResult.Type;

export type UiComponentOfType<Type extends UiComponentType> = Extract<
  NewUiComponent,
  { componentType: Type }
>;

export type UiComponentResultOfType<Type extends UiComponentType> = Extract<
  NewUiComponentResult,
  { componentType: Type }
>;

/** The order a card acts on, or null for a card that acts on none. */
export function uiComponentOrderId(
  component: UiComponentContent,
): string | null {
  switch (component.componentType) {
    case "cancelOrder":
    case "updateShippingAddress":
    case "updateOrder":
    case "return":
    case "orderTracking":
    case "resendReturnLabel":
      return component.input.orderId;
    case "signIn":
    case "products":
    case "discountCode":
    case "productComparison":
    case "wishlist":
    case "backInStock":
      return null;
  }
}

/**
 * Whether posting `newer` replaces `older`, a pending card in the same
 * conversation. A cancel, address or resend-label card replaces one for the
 * same order, and a sign-in or return card replaces any other. Order edits can
 * be pending several at once.
 */
export function replacesUiComponent(
  newer: UiComponentContent,
  older: UiComponentContent,
): boolean {
  if (newer.componentType !== older.componentType) {
    return false;
  }
  switch (newer.componentType) {
    case "cancelOrder":
    case "updateShippingAddress":
    case "resendReturnLabel":
      return uiComponentOrderId(newer) === uiComponentOrderId(older);
    case "signIn":
    case "return":
      return true;
    case "updateOrder":
    case "products":
    case "orderTracking":
    case "discountCode":
    case "productComparison":
    case "wishlist":
    case "backInStock":
      return false;
  }
}
